// ==========================================================================
// Pre-Alert Actions Hooks
// ==========================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  activatePreAlertAction,
  getPreAlertActionDetail,
  getPreAlertActions,
  type PreAlertAction,
} from '@/services/preAlertAction.service';
import { actionKeys } from '@/hooks/useActions';

export const preAlertKeys = {
  all: ['pre-alert-actions'] as const,
  list: () => [...preAlertKeys.all, 'list'] as const,
  detail: (id: number) => [...preAlertKeys.all, 'detail', id] as const,
};

/**
 * List of PreAlert actions to surface in the drawer/banner.
 *
 * Always enabled — the BE returns [] when an alert is active or the user
 * already has an in-progress pre-alert action. Keeping the query active
 * means React Query keeps it in sync via `invalidateQueries()` and
 * `refetchOnWindowFocus`, so the banner re-appears immediately after an
 * alert is cleared without requiring a full app reload.
 */
export function usePreAlertActions() {
  return useQuery({
    queryKey: preAlertKeys.list(),
    queryFn: getPreAlertActions,
    enabled: !!localStorage.getItem('access_token'),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401) return false;
      return failureCount < 2;
    },
  });
}

/** Read-only preview of a single PreAlert action with steps. */
export function usePreAlertActionDetail(actionId: number | null) {
  return useQuery<PreAlertAction>({
    queryKey: preAlertKeys.detail(actionId!),
    queryFn: () => getPreAlertActionDetail(actionId!),
    enabled: !!actionId && !!localStorage.getItem('access_token'),
    staleTime: 60 * 1000,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 404) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Activate a PreAlert action — creates a UserAction (status=active,
 * user_alert_id=null) and invalidates the action lists so the new
 * UserAction appears in the Prepare tab.
 */
export function useActivatePreAlertAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (actionId: number) => activatePreAlertAction(actionId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: preAlertKeys.all });
      await queryClient.invalidateQueries({ queryKey: actionKeys.all });
    },
  });
}
